import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TriangleAlert, TrendingUp, X } from "lucide-react";

const bandColor = (score) =>
  score >= 20 ? "text-red-600" : score >= 13 ? "text-orange-600" : "text-amber-600";

export const PremortemAlerts = ({ canDismiss }) => {
  const [alerts, setAlerts] = useState(null);

  const load = useCallback(() => {
    api
      .get("/premortem-alerts?state=active")
      .then(({ data }) => setAlerts(data.items.slice(0, 5)))
      .catch(() => setAlerts([]));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const dismiss = async (id) => {
    try {
      await api.post(`/premortem-alerts/${id}/dismiss`);
      setAlerts((a) => a.filter((x) => x.alert_id !== id));
      toast.success("Alert dismissed");
    } catch {
      toast.error("Failed to dismiss");
    }
  };

  return (
    <div data-testid="premortem-rail">
      <h2 className="ek-eyebrow flex items-center gap-1">
        <TrendingUp size={12} /> Pre-mortem alerts
      </h2>
      <p className="mt-1 text-sm text-ek-ink/65">30-day projected residual risk.</p>
      <div className="mt-4 space-y-3">
        {alerts === null ? (
          <p className="text-sm text-ek-ink/30">Loading…</p>
        ) : alerts.length === 0 ? (
          <p className="text-sm text-ek-ink/30">No active alerts.</p>
        ) : (
          alerts.map((a) => {
            const delta = (a.projected_residual_score ?? 0) - (a.current_residual_score ?? 0);
            return (
              <div
                key={a.alert_id}
                data-testid="premortem-alert"
                className="rounded-md border border-black/5 bg-white p-4"
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="flex-1 text-sm font-semibold text-ek-ink">{a.risk_title}</p>
                  {canDismiss && (
                    <button
                      onClick={() => dismiss(a.alert_id)}
                      data-testid={`dismiss-alert-${a.alert_id}`}
                      className="text-ek-ink/30 transition-colors hover:text-red-500"
                      title="Dismiss"
                    >
                      <X size={15} />
                    </button>
                  )}
                </div>
                <div className="mt-3 flex items-end justify-between">
                  <div className="flex items-baseline gap-2">
                    <span className="text-xs text-ek-ink/65">{a.current_residual_score ?? "—"}</span>
                    <span className="text-ek-ink/30">→</span>
                    <span className={`font-display text-2xl font-bold ${bandColor(a.projected_residual_score)}`}>
                      {a.projected_residual_score}
                    </span>
                    {delta !== 0 && (
                      <span className={`flex items-center gap-0.5 text-xs font-semibold ${delta > 0 ? "text-red-700" : "text-emerald-700"}`}>
                        <TriangleAlert size={11} /> {delta > 0 ? `+${delta}` : delta}
                      </span>
                    )}
                  </div>
                  <span className="text-[11px] text-ek-ink/65">
                    {Math.round((a.confidence ?? 0) * 100)}% conf
                  </span>
                </div>
                {a.drivers?.[0] && (
                  <p className="mt-2 text-[11px] text-ek-ink/65">{a.drivers[0].feature}</p>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
